
import React, { useRef, useCallback, useMemo }from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import { AgGridReact as AgGridReactType } from 'ag-grid-react';
import { type ColDef, themeAlpine } from 'ag-grid-community';
import '@process-advisor/process-miner/shared/gridStyles.css'
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { openFindingForContext, setFindingContextFromGridRow } from '@process-advisor/process-miner/shared/findingContext';
import { Event } from '@process-advisor/process-miner/api/events';

import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';

const Wrapper = styled.div<{ height: string }>`
    height: ${({ height }) => height};
    width: 100%;
    margin-bottom: 5px;
`;

type SingleRow = {
    id: number;
    [key: string]: any
}

type Props = {
    rows: SingleRow[];
    columns: ColDef[];
    height: string;
    selectionMode: string;
    setApplyEnabled: (value: boolean) => void;
}

export const DataGrid: React.FC<Props> = ({
    rows,
    columns,
    height,
    selectionMode,
    setApplyEnabled,
}) => {

    const grid = useRef<AgGridReactType>(null);
    const sleep = ms => new Promise(r => setTimeout(r, ms));
    
    const menuLabel = (symbol, label) => label;
    const menuIcon = (symbol) => `<span class="pa-ag-menu-icon">${symbol}</span>`;

    const getContextMenuItems = useCallback((params) => {
        const saveFindingMenuItem = {
            name: menuLabel('✚', 'Save Finding'), icon: menuIcon('✚'),
            action: function () {
                openFindingForContext(apiProvider, { source: 'Explorer - Steps', objectType: 'Step', row: params?.node?.data || {} });
            },
        };

        let metrics = [       
            saveFindingMenuItem,
            'separator',
            {
                name: 'Metrics...',
                action: async function () {

                    let from = params.node.data.from_activity_id
                    let to = params.node.data.to_activity_id
                    apiProvider.openStepMetricsModal({
                        data: {
                            fromActivity: params.node.data.from_activity,
                            toActivity: params.node.data.to_activity,
                            avg_comments_customer : params.node.data.avg_comments_customer,
                            avg_comments_internal : params.node.data.avg_comments_internal,
                            avg_duration : params.node.data.avg_duration,
                            avg_worklogs : params.node.data.avg_worklogs,
                            comments_customer : params.node.data.comments_customer,
                            comments_internal : params.node.data.comments_internal,
                            events : params.node.data.events ,
                            tasks: params.node.data.tasks, 
                            max_duration : params.node.data.max_duration,
                            min_duration : params.node.data.min_duration,
                            perc_comments_customer : params.node.data.perc_comments_customer ,
                            perc_comments_internal : params.node.data.perc_comments_internal,
                            perc_total_tasks : params.node.data.perc_total_tasks ,
                            perc_worklog : params.node.data.perc_worklog,
                            std_deviation : params.node.data.std_deviation,
                            worklogs : params.node.data.worklogs,
                            time_spent : params.node.data.time_spent,
                            avg_time_spent: params.node.data.avg_time_spent,
                        },
                    });             
                },
            }
        ]
        let insights = [  {
            name: menuLabel('◆', 'Insights'), icon: menuIcon('◆'),
            subMenu: [
                {
                    name: menuLabel('●', 'Clusters'), icon: menuIcon('●'),
                    action: async function () {
                        openProgressModal({status: 'Processing Clusters'})
                        await sleep(200) // needed to display Progress
                        await apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'step', variant: 0, activity: 0, from: params.node.data.from_activity_id, to: params.node.data.to_activity_id})
                        apiProvider.setDiagramCluster({source: 'step'})
                        closeProgressModal()
                    },
                },
                {
                    name: menuLabel('↗', 'Influencers'), icon: menuIcon('↗'),
                    action: async function () {
                        openProgressModal({status: 'Processing Influencer Insights'})
                        await sleep(200) // needed to display Progress
                        await apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'step', variant: 0, activity: 0, from: params.node.data.from_activity_id, to: params.node.data.to_activity_id})
                        apiProvider.setDiagramInfluenceAnalysis({source: 'step'})
                        closeProgressModal()
                    },
                }, 
                {
                    name: menuLabel('▦', 'Decompose'), icon: menuIcon('▦'),
                    action: function () {
                        apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'step', variant: 0, activity: 0, from: params.node.data.from_activity_id, to: params.node.data.to_activity_id})
                        apiProvider.setDiagramDecompositionTree({source: 'step'})
                    },
                },
                {
                    name: menuLabel('◷', 'Activity Analysis'), icon: menuIcon('◷'),
                    action: function () {
                        apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'step', variant: 0, activity: 0, from: params.node.data.from_activity_id, to: params.node.data.to_activity_id})
                        apiProvider.setTimeInActivitySource({source : 'step'})
                        apiProvider.setAction_Time({});
                    },
                },
                {
                    name: menuLabel('▥', 'Histogram'), icon: menuIcon('▥'),
                    action: function () {
                        apiProvider.generateHistogramArray({variantPaneVariants: false, type: 'step-tasks', variant: 0, activity: 0, from: params.node.data.from_activity_id, to: params.node.data.to_activity_id})
                        apiProvider.histogram({})
                    },
                },
            ]    
            },
        ]

        let tasks = [
            {
                name:  apiProvider.getEntityUpper() + 's',
                subMenu: [
                    {
                        name: menuLabel('≡', 'Detail'), icon: menuIcon('≡'),
                        action: function () {
                            apiProvider.setStepDetailProps({from: params.node.data.from_activity_id, to: params.node.data.to_activity_id })
                            apiProvider.taskDetail({})
                        },
                    },
                    {
                        name: menuLabel('↻', 'History'), icon: menuIcon('↻'),   
                        action: function () {
                            apiProvider.processAnalytics({from: params.node.data.from_activity_id, to: params.node.data.to_activity_id, object_type: 'Step', object_name: params.node.data.step, series_name: apiProvider.getEntityUpper() + 's'})
                        },
                    },  
                    {
                        // custom item
                        name: menuLabel('⟳', 'LifeCycle'), icon: menuIcon('⟳'),
                        action: function () {
                            apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'step', variant: 0, activity: 0, from: params.node.data.from_activity_id, to: params.node.data.to_activity_id})
                            apiProvider.setTasksVisualizationSource({source: 'step'})
                            apiProvider.tasksVisualization()
                            apiProvider.taskLifecycle({})
                        },
                    },    
    
                ],
            },  
        ]
        
        let events = [
            {
                name: menuLabel('◇', 'Events'), icon: menuIcon('◇'),
                subMenu: [
                    {
                        name: menuLabel('≡', 'Detail'), icon: menuIcon('≡'),
                        action: function () {
                            apiProvider.setStepDetailProps({from: params.node.data.from_activity_id, to: params.node.data.to_activity_id })
                            apiProvider.eventDetail({})
                        },
                    },
                    {
                        name: menuLabel('▥', 'Histogram'), icon: menuIcon('▥'),
                        action: function () {
                            apiProvider.generateHistogramArray({variantPaneVariants: false, type: 'step-events', variant: 0, activity: 0, from: params.node.data.from_activity_id, to: params.node.data.to_activity_id})
                            apiProvider.histogram({})
                        },
                    },                  
                ],
            }
        ]
        let cost = [       
            {
                name: 'Cost...',
                action: async function () {

                    let from = params.node.data.from_activity_id
                    let to = params.node.data.to_activity_id
                    let description = 'Cost: ' + params.node.data.from_activity + ' -> ' + params.node.data.to_activity;
                    let costType = 'total';
                    let variantType = 'all_variants';
                    let issueType = 'all_issues';
                    if (apiProvider.filterConditions({category: 'all'}).length > 0) {
                        issueType = 'filtered_issues';
                    }
                    let cost = 0;
                    let mode = 'new';
                    apiProvider.openStepCostModal({
                        data: {
                            description,
                            fromActivity: from,
                            toActivity: to,
                            fromActivityStr: params.node.data.from_activity,
                            toActivityStr: params.node.data.to_activity,
                            cost,
                            costType: 'fixed-cost',
                            variantType,
                            issueType,
                            mode,
                            reductionType: 'N/A', 
                            reductionPercentage: 0,
                            reductionCost: 0,
                            reductionValue: 'hrs'
                        },
                    });             
                },
            }
        ]

        let exportMenu = [
            'separator',
            {
                name: menuLabel('⇩', 'Export'), icon: menuIcon('⇩'),
                subMenu: [
                    {
                        name: menuLabel('CSV', 'CSV Export (all)'), icon: menuIcon('CSV'),
                        action: () => params.api.exportDataAsCsv(
                            {onlySelected: false}
                        ),
                    },
                    {
                        name: menuLabel('CSV', 'CSV Export (selected)'), icon: menuIcon('CSV'),
                        action: () => params.api.exportDataAsCsv(
                            {onlySelected: true}
                          ),
                    },
                    {
                        name: menuLabel('XLS', 'XLS Export (all)'), icon: menuIcon('XLS'),
                        action: () => params.api.exportDataAsExcel(
                            {onlySelected: false}
                        ),
                    },
                    {
                        name: menuLabel('XLS', 'XLS Export (selected)'), icon: menuIcon('XLS'),
                        action: () => params.api.exportDataAsExcel(
                            {onlySelected: true}
                          ),
                    },
                ],
            },        
        ]
        let contextMenu
       
        contextMenu = metrics
        contextMenu = contextMenu.concat(insights)
        contextMenu = contextMenu.concat(tasks)
        contextMenu = contextMenu.concat(events)
        if (apiProvider.getApp() === 'PO') contextMenu = contextMenu.concat(cost)
        contextMenu = contextMenu.concat(exportMenu)
        return contextMenu

      }, []);

    React.useEffect(() => {
        const listener = () => {
            if (grid != null) {
                grid.current.api.forEachNode((rowNode) => {rowNode.setSelected(false);});
            };
        };
        apiProvider.on(Event.resetFilter, listener);

        return () => {
            apiProvider.off(Event.resetFilter, listener);
        };
    }, []);

    const onSelectionChanged = (params) => {
        apiProvider.setStepFilterSelectedData({
            selection: params.api.getSelectedRows(),
        });
        setApplyEnabled(false);
    };

    const onFirstDataRendered = (params) => {
        if (apiProvider.getStepFilterGridState()!= null ) params.api.applyColumnState({state: apiProvider.getStepFilterGridState()});
    };
    
    const onColumnVisible  = (params) => {
        onSaveGridColumnState(params)
    }
    const onColumnResized  = (params) => {
        onSaveGridColumnState(params)
    }
    const onSortChanged  = (params) => {
        onSaveGridColumnState(params)
    }
    const onFilterChanged  = (params) => {
        onSaveGridColumnState(params)
    }

    const onSaveGridColumnState = (params) => {
        let columnState = params.api.getColumnState();
        apiProvider.saveStepFilterGridState({columnState})
    }

    const getRowHeight = () => 25;

    const onRowClickedFindingContext = (params) => {
        setFindingContextFromGridRow(params?.data || params?.node?.data || {}, 'Explorer - Steps', 'Step');
    };

    return (
        <Wrapper height={height}>
            <div
                
                style={{
                    height: '100%',
                    width: '100%',
                    fontSize: '12px',
                    fontFamily: 'system-ui'
,
                }}>
                {selectionMode == 'multiple' ?
                <AgGridReact theme={themeAlpine} 
                    onRowClicked={onRowClickedFindingContext}
                    rowSelection="multiple"
                    onSelectionChanged={onSelectionChanged}
                    onFirstDataRendered={onFirstDataRendered}
                    getRowHeight={getRowHeight}
                    onColumnVisible={onColumnVisible}
                    onColumnResized={onColumnResized}
                    onSortChanged={onSortChanged}       
                    onFilterChanged={onFilterChanged}

                    columnDefs={columns}
                    headerHeight={40}
                    getContextMenuItems={getContextMenuItems}
                    ref={grid}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                        wrapText: true,
                        autoHeight: true,
                    }}
                    sideBar={
                        {
                            toolPanels: [
                                {
                                    id: 'columns',
                                    labelDefault: 'Columns',
                                    labelKey: 'columns',
                                    iconKey: 'columns',
                                    toolPanel: 'agColumnsToolPanel',
                                    toolPanelParams: {
                                        suppressRowGroups: true,
                                        suppressValues: true,
                                        suppressPivotMode: true, 
                                    }
                      
                                },
                                {
                                    id: 'filters',
                                    labelDefault: 'Filters',
                                    labelKey: 'filters',
                                    iconKey: 'filter',
                                    toolPanel: 'agFiltersToolPanel',
                                },
                            ],
                            hiddenByDefault: false,
                            defaultToolPanel: '',
                        }
                    }
                            
                    rowData={rows}
                    animateRows={true}
                />
                :
                <AgGridReact theme={themeAlpine} 
                onRowClicked={onRowClickedFindingContext}
                rowSelection="single"
                onSelectionChanged={onSelectionChanged}
                onFirstDataRendered={onFirstDataRendered}
                getRowHeight={getRowHeight}
                onFilterChanged={onFilterChanged}
                onColumnVisible={onColumnVisible}
                onColumnResized={onColumnResized}
                onSortChanged={onSortChanged}       

                columnDefs={columns}
                headerHeight={40}
                getContextMenuItems={getContextMenuItems}
                ref={grid}
                defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                    wrapText: true,
                    autoHeight: true,
                }}
                sideBar={
                    {
                        toolPanels: [
                            {
                                id: 'columns',
                                labelDefault: 'Columns',
                                labelKey: 'columns',
                                iconKey: 'columns',
                                toolPanel: 'agColumnsToolPanel',
                                toolPanelParams: {
                                    suppressRowGroups: true,
                                    suppressValues: true,
                                    suppressPivotMode: true, 
                                }
                  
                            },
                            {
                                id: 'filters',
                                labelDefault: 'Filters',
                                labelKey: 'filters',
                                iconKey: 'filter',
                                toolPanel: 'agFiltersToolPanel',
                            },
                        ],
                        hiddenByDefault: false,
                        defaultToolPanel: '',
                    }
                }
                        
                rowData={rows}
                animateRows={true}
            />
            }
            </div>
        </Wrapper>
    );
};